import { ComponentProps } from 'react';
import { ValueOf, Theme } from '@redsift/design-system';
import { AnyScale, NumericValue, ScaleValue, StringValue, TicksSpec } from '../../types';

/**
 * Component variant.
 */
export const AxisVariant = {
  none: 'none',
  line: 'line',
  lineTick: 'lineTick',
  tick: 'tick',
  tickValue: 'tickValue',
  default: 'default',
} as const;
export type AxisVariant = ValueOf<typeof AxisVariant>;

export const AxisPosition = {
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
};
export type AxisPosition = ValueOf<typeof AxisPosition>;

/**
 * Component props.
 */
export interface AxisProps extends Omit<ComponentProps<'g'>, 'scale'> {
  /** Disable animations. */
  disableAnimations?: boolean;
  format?: string;
  /** Whether to display grid or not. The length is mandatory and should usually be the length of the other axis. */
  grid?:
    | boolean
    | {
        length?: number;
        stroke?: string;
        strokeWidth?: number;
        strokeDasharray?: string;
        opacity?: number;
      };
  legend?: string;
  legendOffset?: number;
  legendPosition?: 'start' | 'middle' | 'end';
  legendX?: number;
  legendY?: number;
  legendRotation?: number;
  legendStyle?: React.CSSProperties;
  /** Length of the axis. */
  length?: number;
  /** Position of axis. top|bottom means this is an x axis, right|left means this is an y axis. */
  position?: AxisPosition;
  /** Scale (d3.js) used to generate the axis. */
  scale?: AnyScale;
  tickFormat?:
    | ((d: NumericValue) => string | string[])
    | ((d: StringValue) => string | string[])
    | ((d: Date) => string | string[])
    | ((d: ScaleValue) => string | string[]);
  tickPadding?: number;
  tickRotation?: number;
  tickSize?: number;
  tickStyle?: (ticks: any[]) => {
    strokeWidth?: number;
    gridStrokeWidth?: number;
    gridStroke?: string;
  }[];
  tickValues?: TicksSpec;
  tickRemodelling?: (tickValues: any[]) => any[];
  /** Variant. */
  variant?: AxisVariant;
  /** X position. */
  x?: number;
  /** Y position. */
  y?: number;
  /** Chart width, used for horizontal grid. */
  chartWidth?: number;
  /** Chart height, used for horizontal grid. */
  chartHeight?: number;
  /** Section height, used for horizontal grid within a section. */
  sectionHeight?: number;
  /** Color used for tick values. */
  textColor?: string;
  /** Custom method to choose which tick value to color if not all. */
  tickToColor?: (
    tick?: {
      textX: number;
      textY: number;
      lineX: number;
      lineY: number;
      x: number;
      y: number;
      key: string;
      value: ScaleValue;
    },
    index?: number
  ) => boolean;
}

export type StyledAxisProps = Omit<AxisProps, 'areXLabelsRotated' | 'size' | 'scale'> & {
  $theme: Theme;
  $color?: string;
};
